/**
 * @file BulkBillUpload.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills (2026-10-12): choose or drop many supplier
 *              invoice PDFs at once — separate files, or one PDF holding
 *              several bills (each is split into its own bill). PDFs only, up
 *              to the server's limit, 20 MB each, 200 MB at a time.
 */

import React, { useRef, useState } from 'react';
import './BulkAddBills.css';

const MAX_BYTES = 20 * 1024 * 1024;
/** The most one upload takes (the server's cap); the rest are added after. */
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

/** The files that can go, and why the others cannot. */
export function sortFiles(fileList, maxFiles = 50, maxBytes = MAX_UPLOAD_BYTES) {
  const ok = [];
  const refused = [];
  let bytes = 0;
  for (const f of Array.from(fileList || [])) {
    if (!(f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''))) refused.push(`${f.name}: not a PDF`);
    else if (f.size > MAX_BYTES) refused.push(`${f.name}: over 20 MB`);
    else if (bytes + f.size > maxBytes) refused.push(`${f.name}: over ${Math.round(maxBytes / 1024 / 1024)} MB in one go — add it after these`);
    else { ok.push(f); bytes += f.size; }
  }
  if (ok.length > maxFiles) {
    refused.push(`${ok.length - maxFiles} more than ${maxFiles} files — add them after these`);
    ok.length = maxFiles;
  }
  return { ok, refused };
}

function BulkBillUpload({ onUpload, busy = false, maxFiles = 50, compact = false }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [refused, setRefused] = useState([]);

  const take = (list) => {
    const { ok, refused: why } = sortFiles(list, maxFiles);
    setRefused(why);
    if (ok.length) onUpload(ok);
  };

  return (
    <div className={`bulk-bills-upload${compact ? ' compact' : ''}`}>
      <div
        className={`bulk-bills-dropzone${over ? ' over' : ''}${busy ? ' busy' : ''}`}
        role="button"
        tabIndex={0}
        aria-disabled={busy}
        data-write-action
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => { if (!busy && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) take(e.dataTransfer?.files); }}
      >
        <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'sync' : 'upload_file'}</span>
        <div>
          <strong>{busy ? 'Uploading…' : 'Drop supplier invoice PDFs here, or click to choose them'}</strong>
          {!compact && (
            <p>
              Many files at once, or one PDF with several bills in it — each bill is found and read on its own, as
              Add Bill reads an upload. Up to {maxFiles} files at a time, 20 MB each.
            </p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => { take(e.target.files); e.target.value = ''; }}
        />
      </div>
      {refused.length > 0 && (
        <div className="bulk-bills-callout warn">
          <span className="material-symbols-rounded">warning</span>
          <div>Not added: {refused.join('; ')}.</div>
        </div>
      )}
    </div>
  );
}

export default BulkBillUpload;
